import React, { useState, useEffect, useRef } from 'react';

const POLL_INTERVAL = 2000;

async function apiPost(apiBase, endpoint, body) {
  const res = await fetch(`${apiBase}${endpoint}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify(body)
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

async function pollJob(jobId) {
  const res = await fetch(`/api/job/${jobId}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`Failed to poll job: ${res.status}`);
  return res.json();
}

function buildOnshapeUrlFromParams() {
  const params = new URLSearchParams(window.location.search);
  const documentId = params.get('documentId');
  const workspaceId = params.get('workspaceId');
  if (documentId && workspaceId) {
    return `https://cad.onshape.com/documents/${documentId}/w/${workspaceId}`;
  }
  return '';
}

export default function BackfillPropelIds({ apiBase = '/api/admin' }) {
  const [phase, setPhase] = useState('idle'); // idle | scanning | preview | applying | done
  const [urlsText, setUrlsText] = useState(() => buildOnshapeUrlFromParams());
  const [scanResult, setScanResult] = useState(null);   // { parts, summary }
  const [selected, setSelected] = useState({});          // { index: true/false }
  const [applyResult, setApplyResult] = useState(null);  // { results, summary }
  const [jobProgress, setJobProgress] = useState(null);  // { progress, message }
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  function startPolling(jobId, onComplete) {
    pollRef.current = setInterval(async () => {
      try {
        const job = await pollJob(jobId);
        setJobProgress({ progress: job.progress || 0, message: job.message || '' });
        if (job.status === 'completed') {
          clearInterval(pollRef.current);
          onComplete(job.result);
        } else if (job.status === 'failed') {
          clearInterval(pollRef.current);
          setError(job.error || 'Job failed');
          setPhase('idle');
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setError(e.message);
        setPhase('idle');
      }
    }, POLL_INTERVAL);
  }

  async function handleScan() {
    const documentUrls = urlsText.split('\n').map(s => s.trim()).filter(Boolean);
    if (documentUrls.length === 0) {
      setError('Paste at least one Onshape document URL.');
      return;
    }
    setError(null);
    setPhase('scanning');
    setJobProgress({ progress: 0, message: 'Starting scan...' });
    try {
      const { jobId } = await apiPost(apiBase, '/backfill/scan', { documentUrls });
      startPolling(jobId, result => {
        setScanResult(result);
        const initialSelected = {};
        (result.parts || []).forEach((p, i) => {
          initialSelected[i] = p.status === 'MATCHED';
        });
        setSelected(initialSelected);
        setPhase('preview');
      });
    } catch (e) {
      setError(e.message);
      setPhase('idle');
    }
  }

  function toggleRow(index) {
    setSelected(prev => ({ ...prev, [index]: !prev[index] }));
  }

  function toggleAll(checked) {
    const next = {};
    (scanResult?.parts || []).forEach((p, i) => {
      if (p.status === 'MATCHED') next[i] = checked;
    });
    setSelected(next);
  }

  async function handleApply() {
    const selectedParts = (scanResult?.parts || []).filter((_, i) => selected[i]);
    if (selectedParts.length === 0) {
      setError('Select at least one part to update.');
      return;
    }
    if (!window.confirm(`Write Propel ID to ${selectedParts.length} Onshape part(s)? This updates live Onshape data.`)) return;

    setError(null);
    setPhase('applying');
    setJobProgress({ progress: 0, message: 'Starting update...' });
    try {
      const { jobId } = await apiPost(apiBase, '/backfill/apply', { selectedParts });
      startPolling(jobId, result => {
        setApplyResult(result);
        setPhase('done');
      });
    } catch (e) {
      setError(e.message);
      setPhase('preview');
    }
  }

  function handleReset() {
    setPhase('idle');
    setScanResult(null);
    setApplyResult(null);
    setSelected({});
    setJobProgress(null);
    setError(null);
  }

  const matchedCount = Object.values(selected).filter(Boolean).length;
  const allMatchedCount = (scanResult?.parts || []).filter(p => p.status === 'MATCHED').length;
  const allSelected = allMatchedCount > 0 && matchedCount === allMatchedCount;

  return (
    <div className="admin-section">
      <h2>Backfill Propel IDs</h2>
      <p className="section-description">
        Find Onshape parts that have a part number but no Propel ID, match them to existing Propel items, and write the Propel ID back.
        This is a one-time operation for legacy parts created before the integration was available.
      </p>

      {error === 'PROPEL_AUTH_REQUIRED' ? (
        <div style={{
          background: '#fff8e1',
          border: '1px solid #f9a825',
          borderRadius: '6px',
          padding: '1rem 1.25rem',
          marginBottom: '1rem'
        }}>
          <strong>Propel login required</strong>
          <p style={{ margin: '0.5rem 0 0.25rem' }}>
            This tool needs an active Propel session. To set one up:
          </p>
          <ol style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
            <li>Open any Onshape document</li>
            <li>Open the Propel integration panel in the right sidebar</li>
            <li>Log in to Propel when prompted</li>
            <li>Return to this page and try again</li>
          </ol>
        </div>
      ) : error && (
        <div className="alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {/* IDLE */}
      {phase === 'idle' && (
        <div>
          <label className="form-label">
            <strong>Onshape Document URL(s)</strong>
            <span style={{ fontWeight: 'normal', marginLeft: '0.5rem', color: '#666' }}>
              — one URL per line
            </span>
          </label>
          <textarea
            className="form-input"
            style={{ width: '100%', height: '120px', fontFamily: 'monospace', fontSize: '13px', marginTop: '0.5rem' }}
            placeholder="https://cad.onshape.com/documents/abc123/w/def456/e/..."
            value={urlsText}
            onChange={e => setUrlsText(e.target.value)}
          />
          <div style={{ marginTop: '0.75rem' }}>
            <button className="btn btn-primary" onClick={handleScan}>
              Scan for Parts
            </button>
          </div>
        </div>
      )}

      {/* SCANNING */}
      {phase === 'scanning' && (
        <div>
          <ProgressBar progress={jobProgress?.progress || 0} message={jobProgress?.message || 'Scanning...'} />
          <div style={{ marginTop: '1rem' }}>
            <button className="btn btn-secondary" onClick={handleReset}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* PREVIEW */}
      {phase === 'preview' && scanResult && (
        <div>
          <SummaryBanner
            total={scanResult.summary.total}
            matched={scanResult.summary.matched}
            notFound={scanResult.summary.notFound}
          />
          {scanResult.summary.total === 0 ? (
            <div style={{ marginTop: '1rem' }}>
              <p style={{ color: '#555', marginBottom: '0.75rem' }}>
                No parts found with a part number and missing Propel ID. Check that the document URL contains the correct workspace ID, or try a different document.
              </p>
              <button className="btn btn-secondary" onClick={handleReset}>
                Edit URLs and Try Again
              </button>
            </div>
          ) : (
            <>
              <table className="admin-table" style={{ marginTop: '1rem' }}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={e => toggleAll(e.target.checked)}
                        title="Select all matched"
                      />
                    </th>
                    <th>Part Number</th>
                    <th>Part Name</th>
                    <th>Matched Propel Item</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {scanResult.parts.map((part, i) => (
                    <tr key={i} style={{ opacity: part.status === 'NOT_FOUND' ? 0.6 : 1 }}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!!selected[i]}
                          disabled={part.status === 'NOT_FOUND'}
                          onChange={() => toggleRow(i)}
                        />
                      </td>
                      <td><code>{part.partNumber}</code></td>
                      <td>{part.partName}</td>
                      <td>
                        {part.status === 'MATCHED'
                          ? <code>{part.matchedPropelName}</code>
                          : <span style={{ color: '#999', fontStyle: 'italic' }}>Not found in Propel</span>}
                      </td>
                      <td>
                        {part.status === 'MATCHED'
                          ? <span className="status-badge active">Matched</span>
                          : <span className="status-badge inactive">Not Found</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <button className="btn btn-primary" onClick={handleApply} disabled={matchedCount === 0}>
                  Apply Selected ({matchedCount})
                </button>
                <button className="btn btn-secondary" onClick={handleReset}>
                  Start Over
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* APPLYING */}
      {phase === 'applying' && (
        <ProgressBar progress={jobProgress?.progress || 0} message={jobProgress?.message || 'Updating...'} />
      )}

      {/* DONE */}
      {phase === 'done' && applyResult && (
        <div>
          <div className="alert-success" style={{ marginBottom: '1rem' }}>
            Done — {applyResult.summary.succeeded} updated, {applyResult.summary.failed} failed.
          </div>
          {applyResult.summary.failed > 0 && (
            <table className="admin-table" style={{ marginBottom: '1rem' }}>
              <thead>
                <tr>
                  <th>Part Number</th>
                  <th>Result</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {applyResult.results.filter(r => r.writeStatus === 'FAILED').map((r, i) => (
                  <tr key={i}>
                    <td><code>{r.partNumber}</code></td>
                    <td><span className="status-badge inactive">Failed</span></td>
                    <td style={{ color: '#c00', fontSize: '13px' }}>{r.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button className="btn btn-secondary" onClick={handleReset}>
            Run Another Scan
          </button>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ progress, message }) {
  return (
    <div style={{ marginTop: '1rem' }}>
      <div style={{ marginBottom: '0.5rem', color: '#444' }}>{message}</div>
      <div style={{ background: '#e0e0e0', borderRadius: '4px', height: '10px', width: '100%' }}>
        <div style={{
          background: '#4caf50',
          height: '10px',
          borderRadius: '4px',
          width: `${progress}%`,
          transition: 'width 0.3s ease'
        }} />
      </div>
      <div style={{ marginTop: '0.25rem', fontSize: '12px', color: '#888' }}>{progress}%</div>
    </div>
  );
}

function SummaryBanner({ total, matched, notFound }) {
  return (
    <div style={{
      background: '#f5f5f5',
      border: '1px solid #ddd',
      borderRadius: '6px',
      padding: '0.75rem 1rem',
      display: 'flex',
      gap: '1.5rem',
      alignItems: 'center'
    }}>
      <span><strong>{total}</strong> parts found without Propel ID</span>
      <span style={{ color: '#2e7d32' }}>
        <strong>{matched}</strong> matched in Propel
      </span>
      {notFound > 0 && (
        <span style={{ color: '#c62828' }}>
          <strong>{notFound}</strong> not found in Propel
        </span>
      )}
    </div>
  );
}
